import { useCallback, useEffect } from 'react'
import PropTypes from 'prop-types'

import Button from '@/components/Button'
import { useCountdown } from '@/hooks/useCountdown'

import DrkTrimMainLogo from '../../components/svg/DrkTrimMain'
import styles from './ErrorBoundary.scss'

function ErrorFallback({
  error = null,
  refreshPage = true,
  resetError = null,
}) {
  const seconds = useCountdown(5, refreshPage)

  useEffect(() => {
    if (seconds <= 0 && refreshPage) {
      if (resetError) {
        resetError()
      } else {
        window.location.reload()
      }
    }
  }, [seconds, refreshPage, resetError])

  const handleRetry = useCallback(() => {
    if (resetError) {
      resetError()
    } else {
      window.location.reload()
    }
  }, [resetError])

  // Copy error details to clipboard
  const copyErrorDetails = useCallback(() => {
    const errorDetails = `
      Error: ${error?.message || 'No message available'}
      Time: ${new Date().toISOString()}
      URL: ${window.location.href}
      User Agent: ${navigator.userAgent}
    `
    navigator.clipboard
      .writeText(errorDetails)
      .then(() => alert('Error details copied to clipboard'))
      .catch(err => console.error('Failed to copy error details:', err))
  }, [error])

  return (
    <div className={styles.fullPageContainer}>
      <div className={styles.container}>
        <div className={styles.box}>
          <DrkTrimMainLogo
            style={{ marginBottom: '54px' }}
            role='img'
            aria-label='MyAssetMap Logo'
          />
          <h1>Something went wrong!</h1>
          <p>
            <span>It looks like something went wrong.</span>
          </p>
          <p>
            We have saved your work, and will attempt to refresh the page to
            resolve the issue.
          </p>
          <p>
            If you continue to see this message, contact MyAssetMap support at{' '}
            <a href='mailto:support@myassetmap.com'>support@myassetmap.com</a>
          </p>
          {refreshPage && (
            <p>
              Page will automatically refresh in{' '}
              <span id='refresh-timer'>{seconds}</span> second
              {seconds > 1 ? 's' : ''}.{' '}
              <Button
                variant='secondary'
                size='md'
                onClick={handleRetry}
                label='Refresh Page'
              />
            </p>
          )}

          <hr />

          <div className={styles.errorDetails}>
            <p>
              Error: <em>{error ? error.message : 'No message available'}</em>
            </p>
            <Button
              variant='secondary'
              size='md'
              onClick={copyErrorDetails}
              label='Copy Error Details'
            />
          </div>
        </div>
      </div>
    </div>
  )
}

// Add prop types
ErrorFallback.propTypes = {
  error: PropTypes.shape({
    message: PropTypes.string,
    // Add other error properties you expect
  }),
  refreshPage: PropTypes.bool,
  resetError: PropTypes.func,
}

export default ErrorFallback
